import { useState } from "react";
import { toast } from "sonner";
import { Lightbulb } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { countryName } from "@/pages/admin/CountryPicker";

const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const reach = (c) => (!c.length ? "Worldwide" : c.length > 4 ? `${c.slice(0, 4).map(countryName).join(", ")} +${c.length - 4}` : c.map(countryName).join(", "));

function Row({ s, on, toggle, i }) {
  const why = [s.empty_days ? `fills ${s.empty_days} empty day${s.empty_days === 1 ? "" : "s"}` : "covers countries with nothing on those days",
    s.new_countries.length ? `first theme for ${s.new_countries.map(countryName).join(", ")}` : ""].filter(Boolean).join(" · ");
  return (
    <label className="flex gap-3 items-start rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-amber-50/50" data-testid={`suggest-row-${i}`}>
      <input type="checkbox" className="w-4 h-4 mt-1 accent-rf-orange" checked={on} onChange={toggle} data-testid={`suggest-check-${i}`} />
      <span className="text-sm">
        <b className="text-rf-ink">{s.name}</b> <span className="text-rf-ink-soft">· {fmt(s.starts_on)} – {fmt(s.ends_on)} · {reach(s.countries)}</span>
        <span className="block text-xs text-emerald-700 font-semibold">{why}</span>
        {s.check_date && <span className="block text-xs text-amber-700">Date follows the moon — check it before approving.</span>}
      </span>
    </label>
  );
}

// "Suggest themes": built-in holidays that fill gaps in the next 90 days → add the ticked ones as drafts.
export default function SuggestThemesButton({ onAdded }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const open = async () => {
    try {
      const { data: d } = await api.get("/admin/seasonal-themes/suggestions");
      if (!d.suggestions.length) { toast.success("No gaps to fill — every main country has a theme every day for the next 90 days"); return; }
      setData(d); setPicked(new Set(d.suggestions.map((_, i) => i)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load suggestions"); }
  };
  const add = async () => {
    const items = data.suggestions.filter((_, i) => picked.has(i)).map((s) => ({ key: s.key, starts_on: s.starts_on }));
    try {
      const { data: r } = await api.post("/admin/seasonal-themes/suggestions/add", { items });
      toast.success(`Added ${r.created} draft theme${r.created === 1 ? "" : "s"} — generate their artwork next`); setData(null); onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  return (
    <>
      <button type="button" onClick={open} data-testid="seasonal-suggest" className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1"><Lightbulb size={16} /> Suggest themes</button>
      <Dialog open={!!data} onOpenChange={(o) => !o && setData(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto z-[80]" data-testid="suggest-dialog">
          <DialogHeader>
            <DialogTitle>Suggested themes for the gaps</DialogTitle>
            <DialogDescription>{data && `${fmt(data.from)} – ${fmt(data.to)}. National, religious and food days that would fill empty days or countries with no theme. Everything is ticked; untick any you don't want. They're added as drafts.`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">{data?.suggestions.map((s, i) => <Row key={`${s.key}${s.starts_on}`} s={s} i={i} on={picked.has(i)} toggle={() => toggle(i)} />)}</div>
          <button type="button" onClick={add} disabled={!picked.size} data-testid="suggest-add" className="btn-amber !py-2 !px-4 text-sm w-fit disabled:opacity-50">
            Add {picked.size} as draft{picked.size === 1 ? "" : "s"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
